import { differenceInDays, subDays } from 'date-fns';
import { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticChartInterval, useUserReferralActivityQuery } from '../../../../../services';
import { COLORS } from '../../Analytics';
import { ChartWrapper, Loader, StatsItem, TimeSeriesChart } from '../../helpers';
import { ReferralProps } from '../Referral';

import classes from '../../Analytics.module.scss';

const TYPE_LOCALISE_KEY = 'chartTypes.user-referral-activity';

export const UserReferralActivity: FunctionComponent<ReferralProps> = memo(({ from, to }) => {
  const { t } = useTranslation();

  const { data: userReferralActivity, isFetching: isFetchingUserReferralActivity } =
    useUserReferralActivityQuery({
      from,
      to,
      interval: AnalyticChartInterval.AUTO,
    });

  const daysDiff = differenceInDays(new Date(to), new Date(from)) + 1;

  const { data: userReferralActivityPrev, isFetching: isFetchingUserReferralActivityPrev } =
    useUserReferralActivityQuery({
      from: subDays(new Date(from), daysDiff).toISOString(),
      to: subDays(new Date(to), daysDiff).toISOString(),
    });

  const [userReferralActivityLabels, setUserReferralActivityLabels] = useState<string[]>([]);

  useEffect(() => {
    if (userReferralActivity && !userReferralActivityLabels.length) {
      setUserReferralActivityLabels(userReferralActivity.labels);
    }
  }, [userReferralActivity, userReferralActivityLabels.length]);

  const renderStats = useMemo(() => {
    if (isFetchingUserReferralActivity || isFetchingUserReferralActivityPrev) {
      return <Loader stats />;
    }

    if (!userReferralActivity || !userReferralActivityPrev) {
      return null;
    }

    const { datasets } = userReferralActivity;
    const { datasets: datasetsPrev } = userReferralActivityPrev;

    return (
      <div className={classes['analytics__chart-stats']}>
        {datasets.map(({ label, data }, index) => {
          const isDisabled = !userReferralActivityLabels.includes(label);
          const isClickable = userReferralActivityLabels.length > 1 || isDisabled;

          return (
            <StatsItem
              key={label}
              label={label}
              localiseKey={`userReferralActivityLabels.${label}`}
              color={COLORS[index]}
              value={data.reduce((partialSum, value) => partialSum + value, 0)}
              valuePrev={datasetsPrev.find((set) => set.label === label)?.data[0] ?? 0}
              labels={userReferralActivityLabels}
              setLabels={setUserReferralActivityLabels}
              isDisabled={isDisabled}
              isClickable={isClickable}
              isInline
            />
          );
        })}
      </div>
    );
  }, [
    isFetchingUserReferralActivity,
    isFetchingUserReferralActivityPrev,
    userReferralActivity,
    userReferralActivityLabels,
    userReferralActivityPrev,
  ]);

  return (
    <ChartWrapper typeLocaliseKey={TYPE_LOCALISE_KEY} infoLocaliseKey={'userReferralActivity.info'}>
      {renderStats}
      {userReferralActivity && (
        <TimeSeriesChart
          timeSeries={userReferralActivity}
          labels={userReferralActivityLabels}
          localiseKeyLabels={'userReferralActivityLabels'}
          loading={isFetchingUserReferralActivity}
          ariaLabel={t(TYPE_LOCALISE_KEY)}
        />
      )}
    </ChartWrapper>
  );
});
